// components/GanttChart/ColorSettingsModal.js
import React, { useState } from 'react';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { confirmDialog } from '../../utils/confirmDialog';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: white;
  padding: 24px;
  border-radius: 8px;
  width: 90%;
  max-width: 500px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;

  @media (max-width: 900px) {
    width: 100%;
    max-width: 100vw;
    height: 100vh;
    border-radius: 0;
    padding: 16px;
    overflow-y: auto;
  }
`;

const Title = styled.h2`
  margin-top: 0;
  margin-bottom: 16px;
`;

const ColorItem = styled.div`
  display: flex;
  align-items: center;
  padding: 12px;
  border-bottom: 1px solid #eee;
  
  &:hover {
    background-color: #f9f9f9;
  }
`;

const TaskTypeName = styled.div`
  flex: 1;
  font-weight: 500;
`;

const ColorPicker = styled.input.attrs({ type: 'color' })`
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  padding: 0;
  background: none;
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
`;

const Button = styled.button`
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  font-weight: 500;
  cursor: pointer;
`;

const CancelButton = styled(Button)`
  background-color: #f1f1f1;
  color: #333;
  
  &:hover:not(:disabled) {
    background-color: #e1e1e1;
  }
`;

const SaveButton = styled(Button)`
  background-color: #4EA8DE;
  color: white;
  
  &:hover:not(:disabled) {
    background-color: #3b86b5;
  }
  
  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
  }
`;

const ResetButton = styled(Button)`
  background-color: #6c757d;
  color: white;
  margin-right: auto;
  
  &:hover:not(:disabled) {
    background-color: #5a6268;
  }
`;

const DefaultColorSwatches = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 16px;
  padding: 16px;
  background-color: #f8f9fa;
  border-radius: 4px;
`;

const ColorSwatch = styled.div`
  width: 30px;
  height: 30px;
  border-radius: 4px;
  background-color: ${props => props.color};
  cursor: pointer;
  border: 2px solid ${props => props.selected ? '#000' : 'transparent'};
  
  &:hover {
    transform: scale(1.1);
  }
`;

// 기본 색상 팔레트
const DEFAULT_COLORS = [
  '#4EA8DE', '#8884D8', '#82CA9D', '#FFBB28', '#FF8042', 
  '#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#A4DE6C',
  '#F43F5E', '#8B5CF6', '#10B981', '#3B82F6', '#EC4899'
];

// 기본 작업 타입
const DEFAULT_TASK_TYPES = [
  { id: 'development', name: '개발', color: '#4EA8DE' },
  { id: 'design', name: '디자인', color: '#8884D8' },
  { id: 'planning', name: '기획', color: '#82CA9D' },
  { id: 'testing', name: '테스트', color: '#FFBB28' },
  { id: 'deployment', name: '배포', color: '#FF8042' },
  { id: 'research', name: '리서치', color: '#0088FE' },
  { id: 'review', name: '검토', color: '#00C49F' }
];
// 새 타입 추가 폼 스타일
const AddTypeForm = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 20px;
  padding: 12px;
  background-color: #f8f9fa;
  border-radius: 4px;
`;

const Input = styled.input`
  flex: 1;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
`;

const AddButton = styled(Button)`
  background-color: #10B981;
  color: white;
  
  &:hover:not(:disabled) {
    background-color: #059669;
  }
  
  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
  }
`;
export default function ColorSettingsModal({ isOpen, onClose, taskTypes, onSave }) {
    // 편집 가능한 타입 초기화 (기존 값 또는 기본값)
    const [editableTypes, setEditableTypes] = useState(
      taskTypes && taskTypes.length > 0 ? taskTypes : DEFAULT_TASK_TYPES
    );
    
    // 현재 선택된 작업 타입과 색상
    const [selectedTypeId, setSelectedTypeId] = useState(null);
    
    // 새 타입 추가를 위한 상태
    const [newTypeName, setNewTypeName] = useState('');
    const [newTypeColor, setNewTypeColor] = useState('#4EA8DE');
    
    if (!isOpen) return null;
    
    const handleColorChange = (typeId, newColor) => {
      setEditableTypes(
        editableTypes.map(type => 
          type.id === typeId ? { ...type, color: newColor } : type
        )
      );
    };
    
    const handleColorSwatchClick = (color) => {
      if (selectedTypeId) {
        handleColorChange(selectedTypeId, color);
      } else {
        setNewTypeColor(color);
      }
    };
    
    const handleReset = () => {
      setEditableTypes(DEFAULT_TASK_TYPES);
    };
    
    const handleSave = () => {
      onSave(editableTypes);
      onClose();
    };
    
    // 작업 타입 추가 함수
    const handleAddType = () => {
      if (!newTypeName.trim()) {
        return;
      }
      
      // ID 생성 (이름을 소문자로 변환하고 공백을 언더스코어로 대체)
      const newTypeId = newTypeName.trim().toLowerCase().replace(/\s+/g, '_');
      
      // 중복 ID 체크
      if (editableTypes.some(type => type.id === newTypeId)) {
        toast.error('이미 존재하는 작업 타입입니다.');
        return;
      }
      
      // 새 타입 추가
      const newType = {
        id: newTypeId,
        name: newTypeName.trim(),
        color: newTypeColor
      };
      
      setEditableTypes([...editableTypes, newType]);
      
      // 입력 필드 초기화
      setNewTypeName('');
      setNewTypeColor('#4EA8DE');
    };
    
    // 작업 타입 삭제 함수
    const handleDeleteType = async (typeId) => {
      if (await confirmDialog('이 작업 타입을 삭제하시겠습니까?')) {
        setEditableTypes(editableTypes.filter(type => type.id !== typeId));
        
        // 선택된 타입이 삭제된 경우 선택 해제
        if (selectedTypeId === typeId) {
          setSelectedTypeId(null);
        }
      }
    };
    
    return (
      <ModalOverlay onClick={onClose}>
        <ModalContent onClick={(e) => e.stopPropagation()}>
          <Title>작업 유형 색상 설정</Title>
          
          <div>
            {editableTypes.map(type => (
              <ColorItem key={type.id} onClick={() => setSelectedTypeId(type.id)}>
                <TaskTypeName>{type.name}</TaskTypeName>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <ColorPicker 
                    value={type.color}
                    onChange={(e) => handleColorChange(type.id, e.target.value)}
                  />
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteType(type.id);
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#dc3545',
                      cursor: 'pointer',
                      fontSize: '16px'
                    }}
                  >
                    ×
                  </button>
                </div>
              </ColorItem>
            ))}
          </div>
          
          {/* 새 타입 추가 폼 */}
          <AddTypeForm>
            <Input
              type="text"
              placeholder="새 작업 유형 이름"
              value={newTypeName}
              onChange={(e) => setNewTypeName(e.target.value)}
            />
            <ColorPicker 
              value={newTypeColor}
              onChange={(e) => setNewTypeColor(e.target.value)}
            />
            <AddButton onClick={handleAddType} disabled={!newTypeName.trim()}>
              추가
            </AddButton>
          </AddTypeForm>
          
          <DefaultColorSwatches>
            {DEFAULT_COLORS.map((color, index) => (
              <ColorSwatch 
                key={index} 
                color={color}
                selected={(selectedTypeId && editableTypes.find(type => type.id === selectedTypeId)?.color === color) || 
                          (!selectedTypeId && newTypeColor === color)}
                onClick={() => handleColorSwatchClick(color)}
              />
            ))}
          </DefaultColorSwatches>
          
          <ButtonGroup>
            <ResetButton onClick={handleReset}>기본값으로 복원</ResetButton>
            <CancelButton onClick={onClose}>취소</CancelButton>
            <SaveButton onClick={handleSave}>저장</SaveButton>
          </ButtonGroup>
        </ModalContent>
      </ModalOverlay>
    );
  }